import ADIOrderDetailsDeliveryInfo from "@NA/Components/OrderDetails/ADIOrderDetailsDeliveryInfo";
import ADIOrderDetailsOrderLinesList from "@NA/Components/OrderDetails/ADIOrderDetailsOrderLinesList";
import ADIOrderDetailsOrderTotalDisplay from "@NA/Components/OrderDetails/ADIOrderDetailsOrderTotalDisplay";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC, useContext } from "react";
import { css } from "styled-components";
import { connect } from "react-redux";
import { withHistory } from "@insite/mobius/utilities/HistoryContext";

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        userRoles: state.context.session.userRoles,
        enableVat: getSettingsCollection(state).productSettings.enableVat
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface ADIOrderDetailsShipmentViewStyles {
    container?: GridContainerProps;
    deliveryInfoContainer?: GridItemProps;
    cartsContainer?: GridItemProps;
    priceContainer?: GridItemProps;
    shipmentViewContainer?: GridContainerProps;
    shipmentViewHeader?: InjectableCss;
    shipmentViewItemsText?: InjectableCss;
    shipmentViewNoText?: InjectableCss;
    shipmentViewQtyText?: InjectableCss;
    shipmentViewUnitPriceText?: InjectableCss;
    shipmentViewExtPriceText?: InjectableCss;
    shipmentViewHeaderContainer?: InjectableCss;
    shipmentViewDeliveryInfo?: InjectableCss;
    shipmentViewTotalText?: InjectableCss;
}

export const adiOrderDetailsShipmentViewStyles: ADIOrderDetailsShipmentViewStyles = {
    shipmentViewTotalText: {
        css: css`
        width: 26.11%;
        padding-left: 32px;
        @media print{
            width: 20%;
            padding-left: 0px;
        }
        `
    },
    shipmentViewDeliveryInfo: {
        css: css`
        width: 16.67%;
        `
    },
    shipmentViewHeaderContainer: {
        css: css`
        width: 57.825%;
        display: flex;
        align-items: center;
        `
    },
    shipmentViewItemsText: {
        css: css`
        width: 11.493%;
        @media print{
            padding-left: 0px;
        }
        `
    },
    shipmentViewNoText: {
        css: css`
        width: 53.275%;
        padding-left: 28px;
        @media print{
            width: 36%;
            padding-left: 0px;
        }
        `
    },
    shipmentViewQtyText: {
        css: css`
        width: 11.2%;
        padding-left: 4px;
        @media print{
            padding-left: 0px;
        }
        `
    },
    shipmentViewUnitPriceText: {
        css: css`
        width: 18.38%;
        padding-left: 18px;
        @media print{
            padding-left: 10px;
            width: 14%;
        }
        `
    },
    shipmentViewExtPriceText: {
        css: css`
        width: 16.1%;
        @media print{
            padding-left: 0px;
            padding-right: 0px;
        }
        `
    },
    deliveryInfoContainer: {
        width: 2,
        printWidth: 2,
        css: css`
        @media only screen and (max-width: 1024px) {
            flex-basis: 100%;
            max-width: 100%;
        }
        @media print{
            float: left;
            height: 100wh;
        }
        `
    },
    cartsContainer: {
        width: 7,
        printWidth: 6,
        css: css`
        @media only screen and (max-width: 1024px) {
            flex-basis: 100%;
            max-width: 100%;
            flex-direction: column;
        }
        @media print{
            padding-left: 30px;
            float: left;
        }
        `
    },
    priceContainer: {
        width: 3,
        printWidth: 3,
        css: css`
        @media print{
            padding-left: 0px;
            float: right;
        }
        >div[class^="sc"] {
            width: 100%;
        }
        @media only screen and (max-width: 1024px) {
            flex-basis: 100%;
            max-width: 100%;
            justify-content: center;
            margin-top: 12px !important;
            margin-bottom: 16px !important;
            background: ${getColor("common.background")};
        }
        `
    },
    shipmentViewContainer: {
        css: css`
        @media print{
            width: 100%;
            float: left;
            max-width: 100%;
            >${GridOffset}{
                width: 100%;
                flex-direction: row;
            }
        }
        border-top: 1px solid ${getColor("common.border")};
        >${GridOffset}{
            padding-top: 25px;
            @media only screen and (max-width: 1024px) {
                border-top: 0;
            }
        }
        `
    },
    shipmentViewHeader: {
        css: css`
        width: 100%;
        min-height: 40px;
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.13;
        letter-spacing: normal;
        color: ${getColor("text.main")};
        @media only screen and (max-width: 1024px) {
            display: none;
        }
        @media print {
            font-size: 14px;
            color: ${ADIColors.black};
        }
        `
    }
};

const styles = adiOrderDetailsShipmentViewStyles;

const ADIOrderDetailsShipmentView : FC<Props> = ({ userRoles }) => {

    const { value: order } = useContext(OrderStateContext);
    if (!order || !order.orderLines) {
        return null;
    }

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.shipmentViewHeader}>
                <Typography {...styles.shipmentViewDeliveryInfo}>{translate("Delivery Information")}</Typography>
                <StyledWrapper {...styles.shipmentViewHeaderContainer}>
                    <Typography {...styles.shipmentViewItemsText}>{translate("Items")}</Typography>
                    <Typography {...styles.shipmentViewNoText}>{translate("")}</Typography>
                    <Typography {...styles.shipmentViewQtyText}>{translate("Qty")}</Typography>
                    <Typography {...styles.shipmentViewUnitPriceText}>{translate("Unit Price")}</Typography>
                    <Typography {...styles.shipmentViewExtPriceText}>{translate("Ext. Price")}</Typography>
                </StyledWrapper>
                <Typography {...styles.shipmentViewTotalText}>{translate("Total")}</Typography>
            </StyledWrapper>
            {order?.invoiceOrderLines.map((shipment: any, index: number) => {
                return <GridContainer {...styles.shipmentViewContainer} key={index}>
                    <GridItem {...styles.deliveryInfoContainer}>
                        <ADIOrderDetailsDeliveryInfo shipment={shipment} userRoles={userRoles} order={order} />
                    </GridItem>
                    <GridItem {...styles.cartsContainer}>
                        <ADIOrderDetailsOrderLinesList order={order} shipment={shipment} />
                    </GridItem>
                    <GridItem {...styles.priceContainer}>
                        <ADIOrderDetailsOrderTotalDisplay shipment={shipment} />
                    </GridItem>
                </GridContainer>
            })}
        </StyledWrapper>
    )
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)((ADIOrderDetailsShipmentView)),
    definition: {
        group: "Order Details",
        displayName: "ADI Order Details Shipment View"
    },
};

export default widgetModule;
